// /patient/request — the only way a patient gets an appointment now that
// "+ Add appointment" is gone. Previously the portal inserted a notes-c row
// directly, which landed in the wrong schema (AddPatientModal writes to
// whatever schema the client is bound to, not the doctor's) and bypassed both
// the doctor's availability and any approval.
//
// Three steps in one component. The doctor step is skipped when the patient's
// clinic group yields exactly one bookable doctor, because a one-item picker
// is a dead click.
//
// Styling reuses the portal's existing pp-* classes (pages/css/PatientPortal.css)
// so this page adds no CSS of its own.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createRequest,
  fetchDoctors,
  fetchSlots,
} from "../../utils/appointmentsApi";
import { formatLongDate, formatTime, todayString } from "./PatientDataContext";
import AppointmentCalendar from "./AppointmentCalendar";

// How far ahead to offer. Long enough to find a slot, short enough that the
// backend isn't computing a year of grid on every render.
const WINDOW_DAYS = 60;

function datePlus(days) {
  const d = new Date(`${todayString()}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PatientRequestAppointment() {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState(null); // null = still loading
  const [doctor, setDoctor] = useState(null);
  // null = not fetched yet, {} = fetched and genuinely empty. Conflating the
  // two flashed "No times available" for a moment on every load.
  const [slotsByDate, setSlotsByDate] = useState(null);
  // Every PUBLISHED slot with its free/taken flag, so a booked 4:30 can be
  // shown struck through rather than silently vanishing.
  const [statusByDate, setStatusByDate] = useState({});
  // Picking a DAY is always the calendar. This toggle is for a day's TIMES.
  const [timeView, setTimeView] = useState("grid");
  const [month, setMonth] = useState(
    () => new Date(`${todayString()}T00:00:00`)
  );
  const [zone, setZone] = useState(null);
  const [slotDate, setSlotDate] = useState(null);
  const [slotTime, setSlotTime] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDoctors()
      .then((list) => {
        if (cancelled) return;
        setDoctors(list);
        // One doctor means there is no choice to make — go straight to times.
        if (list.length === 1) setDoctor(list[0]);
      })
      .catch((e) => {
        if (!cancelled) {
          setDoctors([]);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSlots = useCallback(() => {
    if (!doctor) return;
    fetchSlots({
      schema: doctor.schema,
      from: todayString(),
      to: datePlus(WINDOW_DAYS),
    })
      .then((d) => {
        setSlotsByDate(d.slots_by_date || {});
        setStatusByDate(d.slot_status_by_date || {});
        setZone(d.timezone || null);
      })
      // Land on {} rather than staying null: a failed fetch must stop the
      // spinner, and the error banner says what went wrong.
      .catch((e) => {
        setSlotsByDate({});
        setStatusByDate({});
        setError(e.message);
      });
  }, [doctor]);

  useEffect(() => {
    // Switching doctor must clear the previous one's grid, or their times
    // linger under the new doctor's name until the fetch returns.
    setSlotsByDate(null);
    setStatusByDate({});
    setSlotDate(null);
    setSlotTime(null);
    loadSlots();
  }, [loadSlots]);

  const loadingSlots = slotsByDate === null;
  // Every published slot for the chosen day, free or taken — the taken ones
  // are shown struck through rather than dropped.
  const daySlots = slotDate ? statusByDate[slotDate] || [] : [];
  const dates = useMemo(
    () => Object.keys(slotsByDate || {}).sort(),
    [slotsByDate]
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await createRequest({
        schema: doctor.schema,
        slotDate,
        slotTime,
        note,
      });
      navigate("../appointments");
    } catch (e) {
      // A lost race is not a failure to apologise for — it means the grid the
      // patient was looking at is stale. Refetch so they see what is left.
      if (e.code === "slot_taken") {
        setError("Sorry — that time was just taken. Here's what's still open.");
        setSlotTime(null);
        loadSlots();
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="pp-page-head">
        <Link to=".." className="pp-back-link">
          ‹ Home
        </Link>
        <h1 className="pp-page-title">Request an appointment</h1>
        <div className="pp-page-sub">
          Pick a time and your clinic will confirm it
        </div>
      </div>

      {error && (
        <div className="pp-card pp-tinted-amber" role="alert">
          {error}
        </div>
      )}

      {doctors === null && <div className="pp-empty">Loading…</div>}

      {doctors && doctors.length === 0 && (
        <div className="pp-empty">
          Your clinic hasn't set up online requests yet. Please call them
          directly.
        </div>
      )}

      {/* STEP 1 — doctor, only when there is a genuine choice */}
      {!doctor && doctors && doctors.length > 1 && (
        <>
          <div className="pp-section-title">Who would you like to see?</div>
          {doctors.map((d) => (
            <div
              key={d.schema}
              className="pp-row"
              role="button"
              tabIndex={0}
              onClick={() => setDoctor(d)}
              onKeyDown={(e) => e.key === "Enter" && setDoctor(d)}
            >
              <div className="pp-row-leading pp-teal">🩺</div>
              <div className="pp-row-body">
                <div className="pp-row-title">{d.physician_name}</div>
              </div>
              <span className="pp-row-chev">›</span>
            </div>
          ))}
        </>
      )}

      {/* STEP 2 — day, then time */}
      {doctor && !slotTime && (
        <>
          <div className="pp-section-title">
            {slotDate ? `Times on ${formatLongDate(slotDate)}` : "Pick a day"}
          </div>

          {/* Without this a patient who picks the wrong doctor is stuck —
              the picker is hidden the moment one is chosen. */}
          {doctors && doctors.length > 1 && (
            <button
              type="button"
              className="pp-btn pp-ghost pp-btn-compact"
              style={{ marginBottom: 12 }}
              onClick={() => setDoctor(null)}
            >
              ‹ {doctor.physician_name} — change doctor
            </button>
          )}

          {loadingSlots && (
            <div className="pp-empty">Finding available times…</div>
          )}

          {/* Only once the fetch has actually returned — otherwise this reads
              as "your doctor has nothing free" for the moment before the
              times arrive. */}
          {!loadingSlots && !slotDate && dates.length === 0 && (
            <div className="pp-empty">
              No times available in the next {WINDOW_DAYS} days.
            </div>
          )}

          {/* Picking a DAY is always the calendar — a 60-row list is a poor
              way to find next Tuesday, and there is nothing the list did that
              the calendar doesn't. */}
          {!loadingSlots && !slotDate && dates.length > 0 && (
            <AppointmentCalendar
              monthDate={month}
              onMonthChange={setMonth}
              slotsByDate={slotsByDate}
              statusByDate={statusByDate}
              selectedDate={slotDate}
              onSelectDate={setSlotDate}
              minDate={todayString()}
            />
          )}

          {slotDate && (
            <div className="pp-card">
              {/* Two ways to read a day's TIMES. The grid fits a whole day on
                  screen; the list is easier to scan and to hit on a phone,
                  where 32 small buttons is a lot of aiming. */}
              <div
                className="pp-page-actions"
                role="group"
                aria-label="Time view"
                style={{ marginBottom: 12 }}
              >
                <button
                  type="button"
                  className={`pp-btn pp-btn-compact ${timeView === "grid" ? "pp-teal" : "pp-ghost"}`}
                  aria-pressed={timeView === "grid"}
                  onClick={() => setTimeView("grid")}
                >
                  Grid
                </button>
                <button
                  type="button"
                  className={`pp-btn pp-btn-compact ${timeView === "list" ? "pp-teal" : "pp-ghost"}`}
                  aria-pressed={timeView === "list"}
                  onClick={() => setTimeView("list")}
                >
                  List
                </button>
              </div>

              {daySlots.length === 0 && (
                <div className="pp-row-meta">
                  Nothing is published for this day.
                </div>
              )}

              {timeView === "grid" && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {daySlots.map((slot) => (
                    <button
                      key={slot.time}
                      type="button"
                      className="pp-btn pp-ghost pp-btn-compact"
                      disabled={!slot.free}
                      aria-label={
                        slot.free
                          ? formatTime(slot.time)
                          : `${formatTime(slot.time)} — already booked`
                      }
                      // Taken times stay VISIBLE and struck through. Removing
                      // them made the doctor look unavailable at times they
                      // simply have someone else in, leaving gaps the patient
                      // couldn't explain.
                      style={
                        slot.free
                          ? undefined
                          : {
                              textDecoration: "line-through",
                              opacity: 0.45,
                              cursor: "not-allowed",
                            }
                      }
                      onClick={() => slot.free && setSlotTime(slot.time)}
                    >
                      {formatTime(slot.time)}
                    </button>
                  ))}
                </div>
              )}

              {timeView === "list" && (
                // Bounded so it scrolls in place rather than pushing the rest
                // of the page down by 32 rows.
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                  {daySlots.map((slot) => (
                    <div
                      key={slot.time}
                      className="pp-row"
                      role={slot.free ? "button" : undefined}
                      tabIndex={slot.free ? 0 : undefined}
                      aria-label={
                        slot.free
                          ? formatTime(slot.time)
                          : `${formatTime(slot.time)} — already booked`
                      }
                      style={slot.free ? undefined : { opacity: 0.55 }}
                      onClick={() => slot.free && setSlotTime(slot.time)}
                      onKeyDown={(e) =>
                        slot.free && e.key === "Enter" && setSlotTime(slot.time)
                      }
                    >
                      <div className="pp-row-leading">🕐</div>
                      <div className="pp-row-body">
                        <div
                          className="pp-row-title"
                          style={
                            slot.free
                              ? undefined
                              : { textDecoration: "line-through" }
                          }
                        >
                          {formatTime(slot.time)}
                        </div>
                        {!slot.free && (
                          <div className="pp-row-meta">Already booked</div>
                        )}
                      </div>
                      {slot.free && <span className="pp-row-chev">›</span>}
                    </div>
                  ))}
                </div>
              )}

              {daySlots.length > 0 && daySlots.every((s) => !s.free) && (
                <div className="pp-row-meta" style={{ marginTop: 12 }}>
                  Every time that day is taken.
                </div>
              )}
              <button
                type="button"
                className="pp-btn pp-ghost"
                style={{ marginTop: 16 }}
                onClick={() => setSlotDate(null)}
              >
                ‹ Another day
              </button>
            </div>
          )}
        </>
      )}

      {/* STEP 3 — confirm */}
      {doctor && slotTime && (
        <div className="pp-card">
          <div className="pp-visit-title">{doctor.physician_name}</div>
          <div className="pp-visit-when">
            {formatLongDate(slotDate)} · {formatTime(slotTime)}
            {/* The clinic's zone, shown because a patient in another state
                would otherwise read the wall-clock time as their own. */}
            {zone ? ` (${zone})` : ""}
          </div>

          <label className="pp-input" style={{ marginTop: 16 }}>
            <span className="pp-input-lbl">
              What would you like to be seen about? (optional)
            </span>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          <div
            style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}
          >
            <button
              type="button"
              className="pp-btn pp-coral"
              disabled={busy}
              onClick={submit}
            >
              {busy ? "Sending…" : "Send request"}
            </button>
            <button
              type="button"
              className="pp-btn pp-ghost"
              onClick={() => setSlotTime(null)}
            >
              Pick another time
            </button>
          </div>
        </div>
      )}
    </>
  );
}
